import { Lead } from "@justhome/common/api/core";
import { EMAIL_PATTERN, PHONE_PATTERN } from "@justhome/common/constants";
import { useIsMobile, usePartner } from "@justhome/common/hooks";
import { Checkbox, InlineNotification, TextField } from "@justhome/react-ui";
import clsx from "clsx";
import { FC, MouseEventHandler } from "react";
import { DeepPartial, useForm } from "react-hook-form";
import { useTranslation } from "../../lib/hooks/useTranslations";
import FormButtons from "../FormButtons";

export type LeadWallFormValue = Lead & {
  explicitOptIn?: boolean;
};
interface LeadWallFormProps {
  defaultValues?: DeepPartial<LeadWallFormValue>;
  onSubmit: (value: Lead) => void;
  isLoading: boolean;
  isError: boolean;
  onPrevious: MouseEventHandler;
  className?: string;
  hasExplicitOptIn?: boolean;
}

const LeadWallForm: FC<LeadWallFormProps> = ({
  defaultValues,
  onSubmit,
  isLoading,
  isError,
  onPrevious,
  className,
  hasExplicitOptIn,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadWallFormValue>({
    defaultValues: {
      ...defaultValues,
      action: "CONTACT_WALL",
    },
    mode: "onBlur",
  });
  const isMobile = useIsMobile();
  const { partner } = usePartner();

  const onSubmitWrapper = (values: LeadWallFormValue) => {
    const { explicitOptIn: _, ...filteredValues } = values;
    onSubmit(filteredValues);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitWrapper)}
      className={clsx(className, "grid grid-cols-2 items-start gap-s m:gap-2s")}
    >
      <TextField
        data-testid="email"
        className="col-span-2"
        autoFocus={!isMobile}
        size="md"
        disabled={isLoading}
        {...register("email", {
          required: t("error_fill_in"),
          pattern: {
            value: EMAIL_PATTERN,
            message: t("email_validation_error", { ns: "common" }),
          },
        })}
        label={t("opt_in_form_email_label")}
        placeholder={t("opt_in_form_email_placeholder")}
        errorMessage={errors.email?.message}
        inputMode="email"
        autoCapitalize="off"
        enterKeyHint="next"
      />
      <TextField
        data-testid="firstName"
        className="col-span-1 m:col-span-2"
        disabled={isLoading}
        {...register("firstName", {
          required: t("error_fill_in"),
        })}
        label={t("opt_in_form_first_name_label")}
        placeholder={t("opt_in_form_first_name_placeholder")}
        errorMessage={errors.firstName?.message}
        enterKeyHint="next"
      />
      <TextField
        data-testid="lastName"
        className="col-span-1 m:col-span-2"
        disabled={isLoading}
        {...register("lastName", {
          required: t("error_fill_in"),
        })}
        label={t("opt_in_form_last_name_label")}
        placeholder={t("opt_in_form_last_name_placeholder")}
        errorMessage={errors.lastName?.message}
        enterKeyHint="next"
      />
      <TextField
        data-testid="phone"
        className="col-span-2"
        disabled={isLoading}
        {...register("phone", {
          required: t("error_fill_in"),
          pattern: {
            value: PHONE_PATTERN,
            message: t("error_phone_number"),
          },
        })}
        type="phone"
        label={t("opt_in_form_phone_number_label")}
        errorMessage={errors.phone?.message}
        enterKeyHint="send"
      />
      {hasExplicitOptIn ? (
        <div className="col-span-2 mb-2xs">
          <p
            className="mb-3s text-app-body-2"
            dangerouslySetInnerHTML={{
              __html: t("form_consent_terms_partner"),
            }}
          />
          <Checkbox
            size="sm"
            className="mb-3s"
            {...register("explicitOptIn", {
              required: t("error_fill_in"),
            })}
            errorMessage={errors.explicitOptIn?.message}
          >
            {t("partner_explicit_opt_in", {
              partner: partner?.name || "Justhome GmbH",
            })}
          </Checkbox>
        </div>
      ) : (
        <div
          className="mt-2xs text-app-body-2 col-span-2"
          dangerouslySetInnerHTML={{
            __html: t(
              !partner ? "form_consent_terms" : "form_consent_terms_partner",
            ),
          }}
        />
      )}
      {isError && (
        <InlineNotification className="mt-2s mb-s col-span-2" type="error">
          {t("generic_error")}
        </InlineNotification>
      )}
      <FormButtons
        className="col-span-2"
        onPrevious={onPrevious}
        isLoading={isLoading}
      />
    </form>
  );
};

export default LeadWallForm;
